import React, { useEffect, useState, useCallback } from 'react';

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const ReceiptIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
    <path d="M8 10h8" /><path d="M8 14h4" />
  </svg>
);

import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { isPlanVisible } from '../config/featureFlags';
import { formatDate } from '../utils/formatDate';

const PLAN_ORDER = ['free', 'starter', 'growth', 'pro'];

function ConfirmModal({ open, title, children, confirmText, confirmStyle, onConfirm, onCancel, loading }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
          >
            ✕
          </button>
        </div>
        <div style={{ marginBottom: 24 }}>{children}</div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn"
            style={confirmStyle || { background: 'var(--primary)', color: 'white' }}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <span className="spinner" /> : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function PasswordConfirmModal({ open, title, description, onConfirm, onCancel, loading }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPassword('');
      setShowPassword(false);
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Password is required');
      return;
    }
    setError('');
    try {
      await onConfirm(password);
    } catch (err) {
      setError(err.message || 'Verification failed');
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
          >
            ✕
          </button>
        </div>
        {description && (
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>{description}</p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Confirm your password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoFocus
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>
          {error && (
            <div style={{ fontSize: 13, color: '#DC2626', marginBottom: 12 }}>{error}</div>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn"
              style={{ background: '#DC2626', color: 'white' }}
              disabled={loading || !password}
            >
              {loading ? <span className="spinner" /> : 'Confirm'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UpgradePreviewModal({ open, preview, targetPlanName, onConfirm, onCancel, loading }) {
  if (!open || !preview) return null;

  const remaining = preview.remaining;

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Upgrade to {targetPlanName}</h2>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
          >
            ✕
          </button>
        </div>

        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#166534', marginBottom: 8 }}>
            Your remaining credits will carry forward
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {remaining?.email && (
              <div style={{ fontSize: 13, color: '#166534' }}>
                Emails remaining: <strong>{remaining.email.remaining === Infinity ? 'Unlimited' : remaining.email.remaining.toLocaleString()}</strong>
              </div>
            )}
            {remaining?.sms && remaining.sms.limit > 0 && (
              <div style={{ fontSize: 13, color: '#166534' }}>
                SMS remaining: <strong>{remaining.sms.remaining === Infinity ? 'Unlimited' : remaining.sms.remaining.toLocaleString()}</strong>
              </div>
            )}
          </div>
        </div>

        <div style={{ background: '#EDE9FE', border: '1px solid #DDD6FE', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#5B21B6', marginBottom: 8 }}>
            New {targetPlanName} plan quota
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{ fontSize: 13, color: '#5B21B6' }}>
              Emails: <strong>{preview.newQuota.email === Infinity ? 'Unlimited' : preview.newQuota.email.toLocaleString()}/month</strong>
            </div>
            {preview.newQuota.sms > 0 && (
              <div style={{ fontSize: 13, color: '#5B21B6' }}>
                SMS: <strong>{preview.newQuota.sms === Infinity ? 'Unlimited' : preview.newQuota.sms.toLocaleString()}/month</strong>
              </div>
            )}
          </div>
        </div>

        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
          Your new plan will activate immediately. Remaining credits from your current cycle will be added to the new quota.
        </p>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={loading}>
            {loading ? <span className="spinner" /> : `Upgrade to ${targetPlanName}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BillModal({ open, onClose }) {
  const [billingMonths, setBillingMonths] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [bill, setBill] = useState(null);
  const [loadingMonths, setLoadingMonths] = useState(true);
  const [loadingBill, setLoadingBill] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!open) return;
    setLoadingMonths(true);
    setError('');
    setBill(null);
    setSelectedPeriod(null);
    api.getInvoices(page, 20)
      .then((data) => {
        const options = (data.invoices || []).map((item) => ({
          period: item.id,
          label: `${formatDate(item.invoice.periodStart)} – ${formatDate(item.invoice.periodEnd)} (${item.invoice.status})`,
        }));
        setBillingMonths(options);
        setTotalPages(data.totalPages || 1);
        if (options.length > 0) {
          setSelectedPeriod(options[0].period);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingMonths(false));
  }, [open, page]);

  useEffect(() => {
    if (!selectedPeriod) return;
    setLoadingBill(true);
    setError('');
    api.getInvoice(selectedPeriod)
      .then(setBill)
      .catch((err) => setError(err.message))
      .finally(() => setLoadingBill(false));
  }, [selectedPeriod]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const blob = await api.downloadInvoicePdf(selectedPeriod);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LoopVisitor-Invoice-${bill?.invoice?.number || 'bill'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  if (!open) return null;

  const fmt = (amount, sym) => {
    if (amount == null) return '';
    return `${sym}${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const invoiceStatus = (bill?.invoice?.status || 'issued').toLowerCase();
  const invoicePaid = invoiceStatus === 'paid';
  const statusColor = invoicePaid ? '#059669' : invoiceStatus === 'cancelled' || invoiceStatus === 'expired' ? '#DC2626' : '#B45309';
  const statusBackground = invoicePaid ? '#ECFDF5' : invoiceStatus === 'cancelled' || invoiceStatus === 'expired' ? '#FEF2F2' : '#FFFBEB';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680, width: '100%', padding: 0, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Fixed top bar with month selector + download */}
        <div style={{
          background: 'linear-gradient(135deg, #6C5CE7 0%, #a855f7 100%)',
          padding: '20px 28px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>Invoices</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Select a billing period</div>
            </div>
            {!loadingMonths && billingMonths.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>‹</button>
                <select
                  value={selectedPeriod || ''}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  style={{
                    padding: '8px 12px', borderRadius: 8, border: '2px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', outline: 'none', backdropFilter: 'blur(4px)', minWidth: 180,
                  }}
                >
                  {billingMonths.map((m) => (
                    <option key={m.period} value={m.period} style={{ color: '#333', background: '#fff' }}>{m.label}</option>
                  ))}
                </select>
                <button className="btn btn-sm" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>›</button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {bill && (
              <button
                onClick={handleDownloadPdf}
                disabled={downloading}
                aria-label="Download invoice PDF"
                title="Download invoice PDF"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 38, height: 38, padding: 0, borderRadius: 8,
                  background: '#fff', color: '#6C5CE7',
                  border: 'none',
                  cursor: downloading ? 'wait' : 'pointer',
                  opacity: downloading ? 0.7 : 1,
                  transition: 'opacity 0.2s, transform 0.1s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                {downloading ? (
                  <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                ) : (
                  <DownloadIcon />
                )}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6,
                color: '#fff', fontSize: 18, padding: '4px 10px', cursor: 'pointer',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ overflow: 'auto', flex: 1, padding: '24px 28px 28px' }}>
          {loadingMonths ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <span className="spinner" style={{ width: 28, height: 28 }} />
              <p style={{ marginTop: 14, color: 'var(--text-muted)', fontSize: 13 }}>Loading billing history...</p>
            </div>
          ) : error && !bill ? (
            <div>
              <div className="alert alert-error">{error}</div>
            </div>
          ) : loadingBill ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <span className="spinner" style={{ width: 28, height: 28 }} />
              <p style={{ marginTop: 14, color: 'var(--text-muted)', fontSize: 13 }}>Loading invoice...</p>
            </div>
          ) : bill ? (
            <>
              {/* Invoice header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invoice</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{bill.invoice.number}</div>
                </div>
                <span style={{
                  display: 'inline-block', background: statusBackground, color: statusColor,
                  fontSize: 11, fontWeight: 700, padding: '5px 14px', borderRadius: 20,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>{invoiceStatus.replaceAll('_', ' ')}</span>
              </div>

              {/* Meta row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20, background: 'var(--bg)', borderRadius: 10, padding: 14 }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Invoice Date</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{formatDate(bill.invoice.date)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Period Start</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{formatDate(bill.invoice.periodStart)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Period End</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{formatDate(bill.invoice.periodEnd)}</div>
                </div>
              </div>

              {/* Bill From / Bill To */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>From</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 3 }}>{bill.company.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    {bill.company.email}<br />
                    {bill.company.website}
                  </div>
                </div>
                <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Bill To</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 3 }}>{bill.customer.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    {bill.customer.email}
                    {bill.customer.phone && <><br />{bill.customer.phone}</>}
                    {bill.customer.country && <><br />{bill.customer.country}</>}
                  </div>
                </div>
              </div>

              {/* Items table */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 60px 100px 100px',
                  background: 'var(--bg)', padding: '10px 14px',
                  fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                  <div>Description</div>
                  <div style={{ textAlign: 'center' }}>Qty</div>
                  <div style={{ textAlign: 'right' }}>Unit Price</div>
                  <div style={{ textAlign: 'right' }}>Amount</div>
                </div>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 60px 100px 100px',
                  padding: '12px 14px', alignItems: 'center',
                  borderTop: '1px solid var(--border)',
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      {bill.plan.name} Plan — monthly subscription
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {formatDate(bill.invoice.periodStart)} – {formatDate(bill.invoice.periodEnd)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text)' }}>1</div>
                  <div style={{ textAlign: 'right', fontSize: 13, color: 'var(--text)' }}>{fmt(bill.billing.subtotal, bill.billing.symbol)}</div>
                  <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{fmt(bill.billing.subtotal, bill.billing.symbol)}</div>
                </div>
              </div>

              {/* Totals */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <div style={{ width: 250 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                    <span>Subtotal</span>
                    <span>{fmt(bill.billing.subtotal, bill.billing.symbol)}</span>
                  </div>
                  <div style={{ borderTop: '2px solid var(--border)', marginTop: 4, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                    <span>Total</span>
                    <span>{fmt(bill.billing.total, bill.billing.symbol)}</span>
                  </div>
                  {invoicePaid && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, fontWeight: 600, color: '#059669' }}>
                      <span>Amount Paid</span>
                      <span>{fmt(bill.billing.total, bill.billing.symbol)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment info */}
              <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
                <div style={{ color: 'var(--text-muted)' }}>Payment Method: <span style={{ color: 'var(--text)', fontWeight: 500 }}>{bill.payment.method}</span></div>
                <div style={{ color: 'var(--text-muted)' }}>Status: <span style={{ color: statusColor, fontWeight: 600, textTransform: 'capitalize' }}>{invoiceStatus.replaceAll('_', ' ')}</span></div>
                {bill.payment.subscriptionId && <div style={{ color: 'var(--text-muted)' }}>Subscription ID: <span style={{ color: 'var(--text)', fontWeight: 500, fontSize: 11 }}>{bill.payment.subscriptionId}</span></div>}
                <div style={{ color: 'var(--text-muted)' }}>Currency: <span style={{ color: 'var(--text)', fontWeight: 500 }}>{bill.billing.currency}</span></div>
              </div>

              {/* Footer note */}
              <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
                This is a computer-generated invoice and does not require a physical signature.
              </p>
            </>
          ) : billingMonths.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📄</div>
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No invoices found yet.</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, description, action, children }) {
  return (
    <div className="section">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2>{title}</h2>
          {description && <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>{description}</p>}
        </div>
        {action}
      </div>
      <div className="card">{children}</div>
    </div>
  );
}

export default function Subscription() {
  const { user, isDemo, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState({ type: '', text: '' });
  const [planLoading, setPlanLoading] = useState('');
  const [subStatus, setSubStatus] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [downgradeModal, setDowngradeModal] = useState(null);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [billModal, setBillModal] = useState(false);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      setMessage({ type: 'success', text: 'Payment successful! Your plan has been upgraded.' });
    } else if (checkout === 'cancel') {
      setMessage({ type: 'error', text: 'Checkout was cancelled.' });
    }
  }, [searchParams]);

  useEffect(() => {
    if (isDemo) {
      api.getPublicPlans('United States')
        .then(setPlans)
        .catch(console.error)
        .finally(() => setLoading(false));
      return;
    }

    const loadPlansFallback = () =>
      Promise.all([
        api.getPlans(),
        user?.plan !== 'free' ? api.getSubscriptionStatus() : Promise.resolve(null),
      ]).then(([plansData, statusData]) => {
        setPlans(plansData);
        if (statusData) setSubStatus(statusData);
      });

    api.getSubscriptionPageData()
      .then((data) => {
        setPlans({ plans: data.plans, region: data.region });
        if (data.status) {
          setSubStatus(data.status);
          if (data.status.plan && data.status.plan !== user?.plan) updateUser({ plan: data.status.plan });
        }
      })
      .catch(() => loadPlansFallback().catch(console.error))
      .finally(() => setLoading(false));
  }, [isDemo, user?.plan]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const openRazorpayCheckout = (subscriptionId, razorpayKeyId, plan) => {
    return new Promise((resolve, reject) => {
      const options = {
        key: razorpayKeyId,
        subscription_id: subscriptionId,
        name: 'LoopVisitor',
        description: `${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan - monthly subscription`,
        handler: (response) => resolve(response),
        modal: { ondismiss: () => reject(new Error('Payment cancelled')) },
        theme: { color: '#6C5CE7' },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    });
  };

  const handlePlanChange = async (targetPlanId) => {
    if (targetPlanId === 'custom') {
      navigate('/support');
      return;
    }

    if (isDemo) {
      updateUser({ plan: targetPlanId });
      setMessage({ type: 'success', text: `Switched to ${targetPlanId} plan` });
      return;
    }

    const currentIdx = PLAN_ORDER.indexOf(user?.plan || 'free');
    const targetIdx = PLAN_ORDER.indexOf(targetPlanId);

    if (user?.plan !== 'free' && subStatus?.hasSubscription && targetIdx > currentIdx) {
      setPlanLoading(targetPlanId);
      try {
        const preview = await api.getUpgradePreview(targetPlanId);
        setUpgradePreview(preview);
        setUpgradeModal(targetPlanId);
      } catch (err) {
        setMessage({ type: 'error', text: err.message });
      } finally {
        setPlanLoading('');
      }
      return;
    }

    if (targetPlanId === 'free' && currentIdx > 0 && subStatus?.hasSubscription) {
      setCancelModal(true);
      return;
    }

    if (targetIdx < currentIdx && targetIdx > 0) {
      setDowngradeModal(targetPlanId);
      return;
    }

    await executeSubscription(targetPlanId);
  };

  const executeSubscription = async (targetPlanId) => {
    setPlanLoading(targetPlanId);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Failed to load payment system');

      const result = await api.createSubscription(targetPlanId);
      const response = await openRazorpayCheckout(result.subscriptionId, result.razorpayKeyId, targetPlanId);

      await api.verifyPayment({
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_subscription_id: response.razorpay_subscription_id,
        razorpay_signature: response.razorpay_signature,
        plan: targetPlanId,
      });

      updateUser({ plan: targetPlanId });
      setSubStatus(prev => ({ ...prev, plan: targetPlanId, hasSubscription: true, subscriptionStatus: 'ACTIVE', billingStatus: 'PAID', isPaused: false, pendingDowngrade: null }));
      setMessage({ type: 'success', text: `Successfully subscribed to ${targetPlanId} plan!` });
    } catch (err) {
      if (err.message !== 'Payment cancelled') {
        setMessage({ type: 'error', text: err.message });
      }
    } finally {
      setPlanLoading('');
    }
  };

  const executeUpgrade = async (targetPlanId) => {
    setUpgradeLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Failed to load payment system');

      const result = await api.requestUpgrade(targetPlanId);
      const response = await openRazorpayCheckout(result.subscriptionId, result.razorpayKeyId, targetPlanId);

      await api.verifyPayment({
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_subscription_id: response.razorpay_subscription_id,
        razorpay_signature: response.razorpay_signature,
        plan: targetPlanId,
      });

      updateUser({ plan: targetPlanId });
      setSubStatus(prev => ({ ...prev, plan: targetPlanId, pendingDowngrade: null }));
      setMessage({ type: 'success', text: `Upgraded to ${targetPlanId} plan!` });
      setUpgradeModal(null);
      setUpgradePreview(null);
    } catch (err) {
      if (err.message !== 'Payment cancelled') {
        setMessage({ type: 'error', text: err.message });
      }
    } finally {
      setUpgradeLoading(false);
    }
  };

  const executeDowngrade = async (targetPlanId) => {
    setDowngradeLoading(true);
    try {
      const result = await api.requestDowngrade(targetPlanId);
      setSubStatus(prev => ({ ...prev, pendingDowngrade: targetPlanId, renewalDate: result.renewalDate || prev?.renewalDate }));
      setMessage({ type: 'success', text: result.message });
      setDowngradeModal(null);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setDowngradeLoading(false);
    }
  };

  const handleCancelSubscription = async (password) => {
    if (isDemo) {
      setMessage({ type: 'success', text: 'Subscription cancelled (demo)' });
      setCancelModal(false);
      return;
    }

    setCancelLoading(true);
    try {
      const result = await api.cancelSubscription(password);
      setSubStatus(prev => ({
        ...prev,
        pendingDowngrade: 'free',
        isCancelling: true,
        renewalDate: result.endDate || prev?.renewalDate,
      }));
      setMessage({ type: 'success', text: result.message });
      setCancelModal(false);
    } catch (err) {
      throw err;
    } finally {
      setCancelLoading(false);
    }
  };

  const clearMessage = useCallback(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 8000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  useEffect(clearMessage, [clearMessage]);

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>Subscription</h1>
          <p>Manage your plan and billing</p>
        </div>

        {(user?.plan || 'free') !== 'free' && (
          <div className="section">
            <div className="section-header">
              <div>
                <h2>Current Subscription</h2>
                <div style={{ height: 13, width: 200, background: 'var(--bg)', borderRadius: 4, marginTop: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
              </div>
            </div>
            <div className="card">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ background: 'var(--bg)', borderRadius: 10, padding: 16 }}>
                    <div style={{ height: 12, width: 50, background: 'var(--surface)', borderRadius: 4, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                    <div style={{ height: 20, width: 80, background: 'var(--surface)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <div className="section-header">
            <h2>Available Plans</h2>
          </div>
          <div className="pricing-grid">
            {[1, 2].map(i => (
              <div key={i} className="pricing-card">
                <div style={{ height: 20, width: 80, background: 'var(--bg)', borderRadius: 4, marginBottom: 12, animation: 'pulse 1.5s ease-in-out infinite' }} />
                <div style={{ height: 32, width: 120, background: 'var(--bg)', borderRadius: 4, marginBottom: 20, animation: 'pulse 1.5s ease-in-out infinite' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                  {[1, 2, 3, 4].map(j => (
                    <div key={j} style={{ height: 14, width: `${90 - j * 10}%`, background: 'var(--bg)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
                  ))}
                </div>
                <div style={{ height: 42, width: '100%', background: 'var(--bg)', borderRadius: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const planList = plans?.plans?.filter(p => ['starter', 'growth'].includes(p.id) && isPlanVisible(p.id)) || [];
  const displayPlans = planList.length > 0 ? planList : [];
  const currentPlan = user?.plan || 'free';
  const isCancelling = subStatus?.pendingDowngrade === 'free' || subStatus?.isCancelling;
  const isPaused = !isDemo && Boolean(subStatus?.isPaused);
  const hasBillingIssue = !isDemo && Boolean(subStatus?.isPaymentFailed);

  return (
    <div>
      <div className="page-header">
        <h1>Subscription</h1>
        <p>Manage your plan and billing</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{message.text}</span>
          <button
            onClick={() => setMessage({ type: '', text: '' })}
            style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'inherit', padding: '0 4px', opacity: 0.7 }}
          >
            ✕
          </button>
        </div>
      )}

      {isPaused && (
        <div className="alert" style={{ background: '#FFF1F2', color: '#9F1239', border: '1px solid #FDA4AF', marginBottom: 24 }}>
          <strong>Your subscription payment failed and messaging is paused.</strong> Review requests and reminders will not be sent until you complete a successful subscription checkout. Choose your current plan below to resume.
        </div>
      )}

      {/* Current Subscription Status */}
      {currentPlan !== 'free' && (
        <SectionCard
          title="Current Subscription"
          description={subStatus?.periodStart ? `Billing cycle started ${formatDate(subStatus.periodStart)}` : undefined}
          action={
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!isDemo && subStatus?.hasSubscription && (
                <button
                  className="btn btn-sm"
                  style={{
                    background: '#EDE9FE', color: '#6C5CE7', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                  onClick={() => setBillModal(true)}
                >
                  <ReceiptIcon />
                  View Bill
                </button>
              )}
              {!isDemo && currentPlan !== 'custom' && !isCancelling && (
                <button className="btn btn-sm" style={{ background: '#FEE2E2', color: '#DC2626', fontWeight: 600 }} onClick={() => setCancelModal(true)}>
                  Cancel Subscription
                </button>
              )}
            </div>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Plan</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize' }}>{currentPlan}</div>
            </div>
            <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Status</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: isPaused || isCancelling ? '#DC2626' : '#059669' }}>
                {isPaused ? 'Payment failed — paused' : isCancelling ? 'Cancelling at cycle end' : 'Active'}
              </div>
            </div>
            {subStatus?.renewalDate && (
              <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                  {isCancelling ? 'Access until' : 'Next renewal'}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                  {formatDate(subStatus.renewalDate)}
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {currentPlan === 'free' && !isDemo && (
        <div className="alert" style={{ background: '#EDE9FE', color: '#5B21B6', border: '1px solid #DDD6FE', marginBottom: 24 }}>
          {hasBillingIssue
            ? <>Your renewal has not been confirmed, so the <strong>Free</strong> plan applies until payment succeeds.</>
            : <>You're on the <strong>Free</strong> plan (5 emails and up to 2 customer records per month). Subscribe for higher limits.</>}
          <button className="btn btn-sm" style={{ marginLeft: 12 }} onClick={() => setBillModal(true)}>Invoice History</button>
        </div>
      )}

      {currentPlan === 'custom' && (
        <div className="alert" style={{ background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0', marginBottom: 24 }}>
          You're on a <strong>Custom</strong> plan. Contact your account manager for changes.
        </div>
      )}

      {subStatus?.pendingDowngrade && subStatus.pendingDowngrade !== 'free' && (
        <div className="alert" style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Your plan will change to <strong>{subStatus.pendingDowngrade}</strong> at your next billing cycle{subStatus.renewalDate ? ` on ${formatDate(subStatus.renewalDate)}` : ''}.</span>
          <button
            className="btn btn-sm"
            style={{ background: '#92400E', color: 'white', whiteSpace: 'nowrap' }}
            onClick={async () => {
              try {
                await api.cancelDowngrade();
                setSubStatus(prev => ({ ...prev, pendingDowngrade: null }));
                setMessage({ type: 'success', text: 'Downgrade cancelled. Staying on current plan.' });
              } catch (err) {
                setMessage({ type: 'error', text: err.message });
              }
            }}
          >
            Cancel Downgrade
          </button>
        </div>
      )}

      {/* Plan Cards */}
      {currentPlan !== 'custom' && displayPlans.length > 0 && (
        <div className="section">
          <div className="section-header">
            <h2>Available Plans</h2>
          </div>
          <div className="pricing-grid">
            {displayPlans.map((plan) => {
              const isCustom = plan.id === 'custom';
              const isCurrent = currentPlan === plan.id;
              const currentIdx = PLAN_ORDER.indexOf(currentPlan);
              const planIdx = PLAN_ORDER.indexOf(plan.id);
              const isUpgradeAction = planIdx > currentIdx;
              const isDowngradeAction = planIdx < currentIdx && planIdx > 0;
              const billingUnavailable = plan.id !== 'free' && !isCustom && plan.subscriptionAvailable === false;

              return (
                <div key={plan.id} className={`pricing-card ${isCurrent ? 'current' : ''}`}>
                  <h3>{plan.name}</h3>
                  <div className="price">
                    {plan.displayPrice?.replace('/month', '') || plan.displayPrice}
                    {!isCustom && plan.id !== 'free' && <span>/month</span>}
                  </div>
                  <ul className="features">
                    {(plan.features || []).map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                  {billingUnavailable && (
                    <p style={{ fontSize: 12, color: '#B45309', margin: '0 0 12px' }}>
                      Billing in this currency is temporarily unavailable.
                    </p>
                  )}
                  {isCurrent ? (
                    isPaused ? (
                      <button className="btn btn-primary btn-block" onClick={() => executeSubscription(plan.id)} disabled={!!planLoading}>
                        {planLoading === plan.id ? <span className="spinner" /> : 'Resume Subscription'}
                      </button>
                    ) : (
                      <button className="btn btn-success btn-block" disabled>
                        Current Plan
                      </button>
                    )
                  ) : isCustom ? (
                    <button
                      className="btn btn-block"
                      style={{ background: '#1F2937', color: 'white' }}
                      onClick={() => navigate('/support')}
                    >
                      Talk to Sales
                    </button>
                  ) : isCancelling ? (
                    <button className="btn btn-block" disabled style={{ opacity: 0.5 }}>
                      Subscription cancelling
                    </button>
                  ) : (
                    <button
                      className={`btn btn-block ${isUpgradeAction ? 'btn-primary' : ''}`}
                      style={isDowngradeAction ? { background: '#F3F4F6', color: 'var(--text)' } : undefined}
                      onClick={() => handlePlanChange(plan.id)}
                      disabled={!!planLoading || billingUnavailable}
                    >
                      {billingUnavailable ? 'Temporarily unavailable' : planLoading === plan.id ? <span className="spinner" /> : (
                        currentPlan === 'free'
                          ? `Subscribe to ${plan.name}`
                          : isUpgradeAction
                            ? `Upgrade to ${plan.name}`
                            : `Downgrade to ${plan.name}`
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modals */}
      <PasswordConfirmModal
        open={cancelModal}
        title="Cancel Subscription"
        description={`Your current plan will remain active until ${subStatus?.renewalDate ? formatDate(subStatus.renewalDate) : 'the end of your billing cycle'}. After that, you'll be moved to the Free plan.`}
        onConfirm={handleCancelSubscription}
        onCancel={() => setCancelModal(false)}
        loading={cancelLoading}
      />

      <ConfirmModal
        open={!!downgradeModal}
        title={`Downgrade to ${downgradeModal ? downgradeModal.charAt(0).toUpperCase() + downgradeModal.slice(1) : ''}`}
        confirmText="Confirm Downgrade"
        confirmStyle={{ background: '#F59E0B', color: 'white' }}
        onConfirm={() => executeDowngrade(downgradeModal)}
        onCancel={() => setDowngradeModal(null)}
        loading={downgradeLoading}
      >
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <p style={{ marginBottom: 12 }}>
            Your downgrade will take effect at your <strong>next billing cycle</strong>
            {subStatus?.renewalDate ? ` on ${formatDate(subStatus.renewalDate)}` : ''}.
          </p>
          <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: 12 }}>
            <div style={{ fontWeight: 600, color: '#92400E', fontSize: 13, marginBottom: 4 }}>
              What happens:
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#92400E', fontSize: 13 }}>
              <li>You keep full access to your current plan until the cycle ends</li>
              <li>Your new plan's limits will apply from the next cycle</li>
              <li>You can cancel this downgrade anytime before it takes effect</li>
            </ul>
          </div>
        </div>
      </ConfirmModal>

      <UpgradePreviewModal
        open={!!upgradeModal}
        preview={upgradePreview}
        targetPlanName={upgradeModal ? upgradeModal.charAt(0).toUpperCase() + upgradeModal.slice(1) : ''}
        onConfirm={() => executeUpgrade(upgradeModal)}
        onCancel={() => { setUpgradeModal(null); setUpgradePreview(null); }}
        loading={upgradeLoading}
      />

      <BillModal open={billModal} onClose={() => setBillModal(false)} />
    </div>
  );
}
